import DOMPurify from 'dompurify'

/**
 * Allowed HTML tags for general content sanitization (CMS pages, etc.)
 */
const ALLOWED_HTML_TAGS: string[] = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'a', 'ul', 'ol', 'li',
  'strong', 'em', 'br', 'img',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'blockquote', 'code', 'pre', 'hr',
  'div', 'span',
]

/**
 * Allowed attributes for sanitized HTML content.
 */
const ALLOWED_HTML_ATTRS: string[] = [
  'href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel',
]

/**
 * Additional SVG-specific tags allowed when sanitizing inline SVG content.
 */
const ALLOWED_SVG_TAGS: string[] = [
  'svg', 'path', 'circle', 'ellipse', 'line', 'polygon', 'polyline',
  'rect', 'g', 'defs', 'use', 'symbol', 'text', 'tspan',
  'clippath', 'mask', 'lineargradient', 'radialgradient', 'stop',
]

/**
 * Additional SVG-specific attributes.
 */
const ALLOWED_SVG_ATTRS: string[] = [
  'viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-linecap',
  'stroke-linejoin', 'd', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y',
  'x1', 'y1', 'x2', 'y2', 'width', 'height', 'points',
  'transform', 'opacity', 'fill-rule', 'clip-rule', 'clip-path',
  'offset', 'stop-color', 'stop-opacity', 'gradientUnits',
  'gradientTransform', 'xlink:href',
]

/**
 * Sanitize an HTML string, allowing only safe tags and attributes.
 * Suitable for CMS content, user-generated HTML, etc.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOWED_HTML_TAGS,
    ALLOWED_ATTR: ALLOWED_HTML_ATTRS,
    ALLOW_DATA_ATTR: false,
  })
}

/**
 * Sanitize an inline SVG string, allowing safe SVG tags and attributes
 * in addition to the base HTML whitelist.
 */
export function sanitizeSvg(svg: string): string {
  return DOMPurify.sanitize(svg, {
    ALLOWED_TAGS: [...ALLOWED_HTML_TAGS, ...ALLOWED_SVG_TAGS],
    ALLOWED_ATTR: [...ALLOWED_HTML_ATTRS, ...ALLOWED_SVG_ATTRS],
    ALLOW_DATA_ATTR: false,
  })
}
